import { createContext, useContext, useEffect, useState } from 'react';

export interface AppTheme {
  key: string;
  label: string;
  /** Main colour (drives --app-primary across the app). */
  primary: string;
  /** Darker shade for gradients / hovers (--app-primary-dark). */
  dark: string;
  /** Optional accent (e.g. TVK yellow) → --app-accent. Defaults to primary. */
  accent?: string;
}

export const THEMES: AppTheme[] = [
  {
    key: 'gov-blue',
    label: 'Government Blue',
    primary: '#1e40af',
    dark: '#1e3a8a',
  },
  {
    key: 'emerald',
    label: 'Emerald Green',
    primary: '#047857',
    dark: '#065f46',
  },
  { key: 'indigo', label: 'Royal Indigo', primary: '#4338ca', dark: '#3730a3' },
  { key: 'maroon', label: 'Deep Maroon', primary: '#9d174d', dark: '#831843' },
  { key: 'teal', label: 'Ocean Teal', primary: '#0e7490', dark: '#155e75' },
  { key: 'graphite', label: 'Graphite', primary: '#374151', dark: '#1f2937' },
  {
    key: 'tvk',
    label: 'TVK Red & Yellow',
    primary: '#c81e1e',
    dark: '#9b1515',
    accent: '#f1c40f',
  },
];

const STORAGE_KEY = 'app-theme';

const applyTheme = (t: AppTheme): void => {
  const root = document.documentElement;
  root.style.setProperty('--app-primary', t.primary);
  root.style.setProperty('--app-primary-dark', t.dark);
  root.style.setProperty('--app-primary-contrast', '#ffffff');
  root.style.setProperty('--app-accent', t.accent ?? t.primary);
};

const getStoredTheme = (): AppTheme => {
  const key = (() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  })();
  return THEMES.find((t) => t.key === key) ?? THEMES[0];
};

// Apply the stored theme immediately at module load so there's no colour flash
// before the provider mounts.
applyTheme(getStoredTheme());

interface ThemeContextValue {
  themes: AppTheme[];
  themeKey: string;
  theme: AppTheme;
  setThemeKey: (key: string) => void;
}

const ThemeContext = createContext<ThemeContextValue>(null!);

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [themeKey, setThemeKey] = useState<string>(() => getStoredTheme().key);
  const theme = THEMES.find((t) => t.key === themeKey) ?? THEMES[0];

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme.key);
    } catch {
      /* ignore storage errors */
    }
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{ themes: THEMES, themeKey, theme, setThemeKey }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
